import { easeOutQuad } from "../shared/easing";

export interface FloatingText {
  text: string;
  x: number;
  y: number;
  life: number;
  maxLife: number;
  color: string;
  fontSize: number;
}

export interface FloatingTextSystem {
  items: FloatingText[];
  pool: FloatingText[];
}

export function createFloatingTextSystem(): FloatingTextSystem {
  return { items: [], pool: [] };
}

function obtainFloatingText(system: FloatingTextSystem): FloatingText {
  const reused = system.pool.pop();
  if (reused != null) {
    return reused;
  }
  return {
    text: "",
    x: 0,
    y: 0,
    life: 0,
    maxLife: 0,
    color: "#ffd166",
    fontSize: 12
  };
}

function releaseFloatingText(system: FloatingTextSystem, text: FloatingText): void {
  if (system.pool.length < 256) {
    system.pool.push(text);
  }
}

export function addFloatingText(
  system: FloatingTextSystem,
  text: string,
  x: number,
  y: number,
  color = "#ffd166",
  duration = 0.9,
  fontSize = 12
): void {
  const item = obtainFloatingText(system);
  item.text = text;
  item.x = x;
  item.y = y;
  item.life = duration;
  item.maxLife = duration;
  item.color = color;
  item.fontSize = fontSize;
  system.items.push(item);
}

export function updateFloatingTexts(system: FloatingTextSystem, dt: number): void {
  for (let i = system.items.length - 1; i >= 0; i--) {
    const item = system.items[i];
    item.y -= 30 * dt;
    item.life -= dt;
    if (item.life <= 0) {
      const lastIndex = system.items.length - 1;
      const removed = system.items[i];
      if (i !== lastIndex) {
        system.items[i] = system.items[lastIndex];
      }
      system.items.pop();
      releaseFloatingText(system, removed);
    }
  }
}

export function drawFloatingTexts(
  ctx: CanvasRenderingContext2D,
  system: FloatingTextSystem
): void {
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  for (const item of system.items) {
    const t = Math.max(0, item.life / item.maxLife);
    const alpha = easeOutQuad(t);
    // Scale-up pop: starts at 1.35x and settles to 1.0x over first 30% of life
    const scaleT = Math.min(1, (1 - t) / 0.3);
    const scale = 1 + 0.35 * (1 - easeOutQuad(scaleT));
    const scaledSize = Math.round(item.fontSize * scale);

    ctx.globalAlpha = alpha;
    ctx.font = `bold ${scaledSize}px 'Press Start 2P', monospace`;

    // Dark outline for contrast
    ctx.strokeStyle = "rgba(0, 0, 0, 0.85)";
    ctx.lineWidth = 3;
    ctx.lineJoin = "round";
    ctx.strokeText(item.text, item.x, item.y);

    // Fill
    ctx.fillStyle = item.color;
    ctx.fillText(item.text, item.x, item.y);
  }
  ctx.globalAlpha = 1;
}
